import { useState, useEffect } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { oneDark } from '@codemirror/theme-one-dark';

/**
 * HTML Editor Component
 * 
 * A rich text editor for HTML content with syntax highlighting and live preview
 * 
 * @author SureCost Development Team
 * @version 1.0.0
 */
const HtmlEditor = ({ 
    value = '', 
    onChange, 
    placeholder = 'Enter HTML content...', 
    height = '300px',
    showPreview = true 
}) => {
    const [activeTab, setActiveTab] = useState('editor');
    const [editorValue, setEditorValue] = useState(value);

    useEffect(() => {
        setEditorValue(value);
    }, [value]);

    const handleChange = (val) => {
        setEditorValue(val);
        if (onChange) {
            onChange(val);
        }
    };

    const insertVariable = (variable) => {
        // Insert at cursor position if possible, otherwise append
        const newValue = editorValue + `{${variable}}`;
        handleChange(newValue);
    };

    const insertHtmlTemplate = (templateType) => {
        let template = '';
        switch (templateType) {
            case 'button':
                template = `<div style="margin: 30px 0; text-align: center;">
    <a href="{magicLink}" style="background-color: #1f2937; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: 600;">
        Click Here
    </a>
</div>`;
                break;
            case 'header':
                template = `<h2 style="color: #1f2937; margin-bottom: 20px;">Your Header Text</h2>`;
                break;
            case 'paragraph':
                template = `<p style="color: #374151; line-height: 1.6; margin-bottom: 16px;">Your paragraph text here.</p>`;
                break;
            default:
                return;
        }
        const newValue = editorValue + template;
        handleChange(newValue);
    };

    const formatHtml = () => {
        // Simple HTML formatting - in production you might want to use a proper HTML formatter
        try {
            const formatted = editorValue
                .replace(/></g, '>\n<')
                .replace(/^\s+|\s+$/g, '')
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0)
                .join('\n');
            handleChange(formatted);
        } catch (error) {
            console.error('Error formatting HTML:', error);
        }
    };

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', overflow: 'hidden' }}>
            {/* Header with tabs and tools */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '0.5rem 1rem',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb'
            }}>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                        onClick={() => setActiveTab('editor')}
                        style={{
                            padding: '0.25rem 0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: activeTab === 'editor' ? '#2563eb' : '#6b7280',
                            backgroundColor: activeTab === 'editor' ? '#eff6ff' : 'transparent',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        Editor
                    </button>
                    {showPreview && (
                        <button
                            onClick={() => setActiveTab('preview')}
                            style={{
                                padding: '0.25rem 0.75rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: activeTab === 'preview' ? '#2563eb' : '#6b7280',
                                backgroundColor: activeTab === 'preview' ? '#eff6ff' : 'transparent',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            Preview
                        </button>
                    )}
                </div>

                {/* Toolbar */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {/* Variable insertion buttons */}
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                            onClick={() => insertVariable('magicLink')}
                            title="Insert Magic Link Variable"
                            style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                                e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#6b7280';
                            }}
                        >
                            {'{'}Link{'}'}
                        </button>
                        <button
                            onClick={() => insertVariable('userName')}
                            title="Insert User Name Variable"
                            style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                                e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#6b7280';
                            }}
                        >
                            {'{'}Name{'}'}
                        </button>
                        <button
                            onClick={() => insertVariable('companyName')}
                            title="Insert Company Name Variable"
                            style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                                e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#6b7280';
                            }}
                        >
                            {'{'}Company{'}'}
                        </button>
                    </div>

                    {/* HTML Template buttons */}
                    <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.5rem', paddingLeft: '0.5rem', borderLeft: '1px solid #d1d5db' }}>
                        <button
                            onClick={() => insertHtmlTemplate('button')}
                            title="Insert Button Template"
                            style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                                e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#6b7280';
                            }}
                        >
                            Button
                        </button>
                        <button
                            onClick={() => insertHtmlTemplate('header')}
                            title="Insert Header Template"
                            style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                                e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#6b7280';
                            }}
                        >
                            Header
                        </button>
                        <button
                            onClick={() => insertHtmlTemplate('paragraph')}
                            title="Insert Paragraph Template"
                            style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                                e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#6b7280';
                            }}
                        >
                            Text
                        </button>
                    </div>

                    {/* Format button */}
                    <button
                        onClick={formatHtml}
                        title="Format HTML"
                        style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            marginLeft: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                            e.target.style.color = '#374151';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.color = '#6b7280';
                        }}
                    >
                        Format
                    </button>
                </div>
            </div>

            {/* Content area */}
            <div style={{ position: 'relative' }}>
                {activeTab === 'editor' && (
                    <CodeMirror
                        value={editorValue}
                        onChange={handleChange}
                        extensions={[html()]}
                        theme={undefined} // Use light theme
                        placeholder={placeholder}
                        style={{
                            fontSize: '14px',
                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
                        }}
                        basicSetup={{
                            lineNumbers: true,
                            foldGutter: true,
                            dropCursor: false,
                            allowMultipleSelections: false,
                            indentOnInput: true,
                            bracketMatching: true,
                            closeBrackets: true,
                            autocompletion: true,
                            highlightSelectionMatches: false,
                            searchKeymap: true
                        }}
                        height={height}
                    />
                )}

                {activeTab === 'preview' && showPreview && (
                    <div 
                        style={{
                            height: height,
                            padding: '1rem',
                            backgroundColor: '#f9fafb',
                            overflowY: 'auto'
                        }}
                    >
                        <div 
                            style={{
                                backgroundColor: 'white',
                                padding: '1rem',
                                borderRadius: '0.375rem',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                minHeight: 'calc(100% - 2rem)'
                            }}
                            dangerouslySetInnerHTML={{ 
                                __html: editorValue || '<p style="color: #9ca3af; font-style: italic;">No content to preview</p>' 
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Footer with character count */}
            <div style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb',
                fontSize: '0.75rem',
                color: '#6b7280',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div>
                    HTML Editor - Use variables like {'{'}magicLink{'}'}, {'{'}userName{'}'}, {'{'}companyName{'}'}
                </div>
                <div>
                    {editorValue.length} characters
                </div>
            </div>
        </div>
    );
};

export default HtmlEditor;
